import { useParams } from 'react-router-dom';
import { getComicBySlug } from '@/mocks/comics';
import { useState } from 'react';

export default function ComicInfo() {
  const { slug } = useParams<{ slug: string }>();
  const comic = slug ? getComicBySlug(slug) : undefined;
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');
  const [visibleChapters, setVisibleChapters] = useState(8);

  if (!comic) return null;

  const stats = [
    { label: '总章节数', value: `${comic.totalChapters}话` },
    { label: '累计阅读', value: `${(comic.reads / 10000).toFixed(0)}万` },
    { label: '综合评分', value: `${comic.rating}分` },
    { label: '更新周期', value: comic.updateFrequency },
  ];

  const sortedChapters = [...comic.chapters].sort((a, b) => b.number - a.number);

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 md:gap-16">
          <div className="lg:w-3/5">
            <span className="text-xs font-medium text-foreground-500 tracking-widest">
              作品详情
            </span>

            <div className="mt-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {comic.title}
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                {comic.author} · {comic.status} · {comic.category}
              </p>

              <div className="mt-6">
                <div className="flex items-center gap-1 mb-6">
                  <div className="flex gap-0.5">
                    <button
                      type="button"
                      onClick={() => setActiveTab('info')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeTab === 'info'
                          ? 'bg-foreground-900 text-white'
                          : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                      }`}
                    >
                      作品简介
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('chapters')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeTab === 'chapters'
                          ? 'bg-foreground-900 text-white'
                          : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                      }`}
                    >
                      章节目录
                    </button>
                  </div>
                </div>

                {activeTab === 'info' ? (
                  <div className="space-y-3">
                    <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                      {comic.description}
                    </p>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {comic.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs font-medium"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sortedChapters.slice(0, visibleChapters).map((ch) => (
                      <button
                        key={ch.id}
                        type="button"
                        className="w-full flex items-center justify-between p-3.5 rounded-xl bg-background-50 hover:bg-background-200 transition-colors duration-200 cursor-pointer text-left"
                      >
                        <span className="text-sm font-medium text-foreground-800">
                          {ch.title}
                        </span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          {ch.updatedAt}
                        </span>
                      </button>
                    ))}
                    {visibleChapters < sortedChapters.length && (
                      <button
                        type="button"
                        onClick={() => setVisibleChapters((prev) => prev + 8)}
                        className="w-full py-3 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
                      >
                        查看更多章节 &darr;
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:w-2/5">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="p-5 md:p-6 rounded-2xl bg-background-50 text-center"
                >
                  <span className="font-heading text-2xl md:text-3xl font-black text-foreground-900">
                    {stat.value}
                  </span>
                  <p className="text-xs text-foreground-500 mt-1.5 font-medium">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}